import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axiosInstance from '@/utils/appsettings';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useAuth } from '@/contexts/AuthContext';

// Types
export interface GitHubAccount {
    id: string;
    login: string;
    name: string;
    email: string;
    avatarUrl: string;
    connectedAt: string;
}

export interface GitHubBranchList {
    branches: string[];
    defaultBranch: string | null;
    /** True when the repo has more branches than the server's pagination cap */
    truncated: boolean;
}

export interface GitHubRepository {
    id: string;
    name: string;
    fullName: string;
    description?: string;
    private: boolean;
    url: string;
    htmlUrl: string;
    createdAt: string;
    updatedAt: string;
    language?: string;
    defaultBranch?: string;
    stars?: number;
    forks?: number;
    homepage?: string | null;
    topics?: string[];
}

/** Builder a repo's own files point to, as the server reads them; null when neither builder left a trace */
export type RepositorySource = 'lovable' | 'replit' | null;

/** A non-Node backend found in the repo before import (Joylo runs Node, so the AI would rewrite it) */
export interface RepositoryBackend {
    language: string;
    framework?: string | null;
}

export type RepositoryAccessReason ='ok' | 'not_connected' | 'token_invalid' | 'not_found' | 'unavailable';

export interface RepositoryAccess {
    accessible: boolean;
    reason: RepositoryAccessReason;
    account?: string;
    manageAccessUrl?: string;
    repository: {
        fullName: string;
        name: string;
        owner: string;
        ownerType?: string;
        private: boolean;
        defaultBranch: string;
        description?: string | null;
    } | null;
}

export interface ProjectGitHubConnection {
    connected: boolean;
    repository: {
        id: string;
        name: string;
        fullName: string;
        url: string;
        htmlUrl: string;
        private: boolean;
        exportedAt: string;
        lastSyncAt: string;
    } | null;
}

export type SyncProjectResult =
    | { success: true; conflict?: false; data?: { commitSha?: string; commitUrl?: string; syncedAt?: string; hasNewChanges?: boolean } }
    | { success: false; conflict: true; conflictedFiles: string[]; error?: string }
    | { success: false; conflict?: false; error: string };

interface GitHubContextType {
    // Connection state
    isConnected: boolean;
    githubAccount: GitHubAccount | null;
    /** GitHub's settings page for Joylo's OAuth app, where a user grants or requests access to an organisation */
    manageAccessUrl: string | null;
    isLoading: boolean;

    // Actions
    checkConnection: () => Promise<void>;
    initiateConnection: () => Promise<void>;
    disconnect: () => Promise<void>;

    // Repository management
    createRepository: (params: {
        projectId: string;
        repositoryName: string;
        description?: string;
        isPrivate?: boolean;
        pushHistory?: boolean;
        branch?: string;
    }) => Promise<{ success: boolean; data?: any; error?: string }>;

    linkRepository: (params: { // Add this
        projectId: string;
        repositoryName: string;
        pushHistory?: boolean;
        branch?: string;
    }) => Promise<{ success: boolean; data?: any; error?: string }>;

    exportProject: (params: {
        projectId: string;
        repositoryName: string;
        description?: string;
        isPrivate?: boolean;
        createNew: boolean;
    }) => Promise<{ success: boolean; data?: any; error?: string }>;

    getProjectConnection: (projectId: string) => Promise<ProjectGitHubConnection | null>;
    syncProject: (projectId: string) => Promise<SyncProjectResult>;
    abortSyncConflict: (projectId: string) => Promise<{ success: boolean; error?: string }>;
    disconnectProject: (projectId: string) => Promise<{ success: boolean; error?: string }>;
    listRepositories: (params?: {
        page?: number;
        perPage?: number;
        sort?: string;
        visibility?: string;
    }) => Promise<{ repositories: GitHubRepository[]; total: number } | null>;

    /** Branches plus the repo's default branch as reported by GitHub (null when unknown) */
    listBranchesWithDefault: (repoFullName: string) => Promise<GitHubBranchList>;

    /** Whether the connected account can read the repo, checked the way the clone will be; `unavailable` when GitHub couldn't be asked */
    checkRepositoryAccess: (repoFullName: string) => Promise<RepositoryAccess>;

    /** Builder each repo came from, read from its files (up to 100 per call); empty when the check couldn't run */
    detectRepositorySources: (repoFullNames: string[]) => Promise<Record<string, RepositorySource>>;

    /** The repo's non-Node backend on a branch; null when it has none or the check couldn't run */
    probeRepositoryBackend?: (repoFullName: string, branch: string) => Promise<RepositoryBackend | null>;

    importProject: (params: {
        repositoryFullName: string;
        projectName: string;
        branch?: string;
        workspaceId?: number;
        projectType?: 'fullstack' | 'mobile';
        viaMigrationService?: boolean;
        architectMigration?: boolean;
        silent?: boolean;
    }) => Promise<{ success: boolean; data?: any; error?: string }>;
}
const GitHubContext = createContext<GitHubContextType | undefined>(undefined);

export const useGitHub = () => {
    const context = useContext(GitHubContext);
    if (!context) {
        throw new Error('useGitHub must be used within GitHubProvider');
    }
    return context;
};

interface GitHubProviderProps {
    children: ReactNode;
}

export const GitHubProvider: React.FC<GitHubProviderProps> = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [githubAccount, setGithubAccount] = useState<GitHubAccount | null>(null);
    const [manageAccessUrl, setManageAccessUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();
    const { isAuthenticated, loading: authLoading } = useAuth();

    // Check GitHub connection status — only after auth is confirmed ready
    const checkConnection = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/github/connection');

            if (response.data.success) {
                setIsConnected(response.data.data.connected);
                setGithubAccount(response.data.data.githubAccount);
                setManageAccessUrl(response.data.data.manageAccessUrl ?? null);
            }
        } catch (error: any) {
            setIsConnected(false);
            setGithubAccount(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Wait until AuthContext has finished its session restore before
        // calling the endpoint — prevents a 401 from firing with no token.
        if (!authLoading && isAuthenticated) {
            checkConnection();
        } else if (!authLoading && !isAuthenticated) {
            setIsLoading(false);
        }
    }, [authLoading, isAuthenticated, checkConnection]);

    // Initiate GitHub OAuth connection
    const initiateConnection = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/github/auth/initiate');

            if (response.data.success) {
                const { authorizationUrl, state } = response.data.data;

                // Store state for CSRF protection
                sessionStorage.setItem('github_oauth_state', state);

                // Save current page so the callback can return the user here
                sessionStorage.setItem('github_return_url', window.location.pathname + window.location.search);

                // Redirect to GitHub OAuth
                window.location.href = authorizationUrl;
            }
        } catch (error: any) {
            toast({
                title: 'Connection Failed',
                description: error.response?.data?.detail?.error?.message || 'Failed to connect to GitHub',
                variant: 'destructive',
            });
        }
    }, [toast]);

    // Handle OAuth callback (call this from your callback page/component)
    const handleCallback = useCallback(async (code: string, state: string) => {
        try {
            const storedState = sessionStorage.getItem('github_oauth_state');

            if (state !== storedState) {
                throw new Error('Invalid state parameter');
            }

            const response = await axiosInstance.post('/github/auth/callback', {
                code,
                state,
            });

            if (response.data.success) {
                setIsConnected(true);
                setGithubAccount(response.data.data.githubAccount);

                sessionStorage.removeItem('github_oauth_state');

                toast({
                    title: 'Connected Successfully',
                    description: `Connected to GitHub as ${response.data.data.githubAccount.login}`,
                });

                return true;
            }
        } catch (error: any) {
            toast({
                title: 'Connection Failed',
                description: error.response?.data?.detail?.error?.message || 'Failed to complete GitHub connection',
                variant: 'destructive',
            });
            return false;
        }
    }, [toast]);

    // Disconnect GitHub account
    const disconnect = useCallback(async () => {
        try {
            const response = await axiosInstance.delete('/github/connection');

            if (response.data.success) {
                setIsConnected(false);
                setGithubAccount(null);

                toast({
                    title: 'Disconnected',
                    description: 'GitHub account disconnected successfully',
                });
            }
        } catch (error: any) {
            toast({
                title: 'Disconnection Failed',
                description: error.response?.data?.detail?.error?.message || 'Failed to disconnect GitHub account',
                variant: 'destructive',
            });
        }
    }, [toast]);

    // Export project to GitHub
    const exportProject = useCallback(async (params: {
        projectId: string;
        repositoryName: string;
        description?: string;
        isPrivate?: boolean;
        createNew: boolean;
    }) => {
        try {
            const response = await axiosInstance.post('/github/export', params);

            if (response.data.success) {
                toast({
                    title: 'Export Successful',
                    description: `Project exported to ${response.data.data.repository.fullName}`,
                });

                return { success: true, data: response.data.data };
            }

            return { success: false, error: 'Export failed' };
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail?.error?.message || 'Failed to export project to GitHub';

            toast({
                title: 'Export Failed',
                description: errorMessage,
                variant: 'destructive',
            });

            return { success: false, error: errorMessage };
        }
    }, [toast]);

    // Create repository with history preservation
    const createRepository = useCallback(async (params: {
        projectId: string;
        repositoryName: string;
        description?: string;
        isPrivate?: boolean;
        pushHistory?: boolean;
        branch?: string;
    }) => {
        try {
            const response = await axiosInstance.post(
                `/github/project/${params.projectId}/create-repository`,
                {
                    repositoryName: params.repositoryName,
                    description: params.description,
                    isPrivate: params.isPrivate ?? true,
                    pushHistory: params.pushHistory ?? true,
                    branch: params.branch || 'main'
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                if (data.pushFailed) {
                    toast({
                        title: 'Repository Created',
                        description: `${data.fullName} was created on GitHub, but your code could not be pushed. You can try again from Repository Settings.`,
                        variant: 'destructive',
                    });
                } else {
                    toast({
                        title: 'Repository Created',
                        description: `Created ${data.fullName} with ${data.commitsPreserved || 0} commits preserved`,
                    });
                }

                return { success: true, data };
            }

            return { success: false, error: 'Repository creation failed' };
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail?.error?.message || 'Failed to create repository';

            toast({
                title: 'Repository Creation Failed',
                description: errorMessage,
                variant: 'destructive',
            });

            return { success: false, error: errorMessage };
        }
    }, [toast]);

    // Link existing repository
    const linkRepository = useCallback(async (params: {
        projectId: string;
        repositoryName: string;
        pushHistory?: boolean;
        branch?: string;
    }) => {
        try {
            const response = await axiosInstance.post(
                `/github/project/${params.projectId}/link-repository`,
                {
                    repositoryName: params.repositoryName,
                    pushHistory: params.pushHistory ?? true,
                    branch: params.branch || 'main'
                }
            );

            if (response.data.success) {
                const data = response.data.data;
                if (data.pushFailed) {
                    toast({
                        title: 'Repository Linked',
                        description: `Linked to ${data.fullName}, but your code could not be pushed. You can try again from Repository Settings.`,
                        variant: 'destructive',
                    });
                } else {
                    toast({
                        title: 'Repository Linked',
                        description: `Linked to ${data.fullName} with ${data.commitsPreserved || 0} commits preserved`,
                    });
                }

                return { success: true, data };
            }

            return { success: false, error: 'Repository linking failed' };
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail?.error?.message || 'Failed to link repository';

            toast({
                title: 'Repository Linking Failed',
                description: errorMessage,
                variant: 'destructive',
            });

            return { success: false, error: errorMessage };
        }
    }, [toast]);

    // Get project GitHub connection
    const getProjectConnection = useCallback(async (projectId: string): Promise<ProjectGitHubConnection | null> => {
        try {
            const response = await axiosInstance.get(`/github/project/${projectId}/connection`);

            if (response.data.success) {
                return response.data.data;
            }

            return null;
        } catch (error: any) {
            return null;
        }
    }, []);

    // Sync project with GitHub — pulls outside changes, then pushes. Commit
    // message is backend-generated; conflicts are returned (not thrown) so the
    // sync modal can offer AI-assisted resolution instead of a generic error toast.
    const syncProject = useCallback(async (projectId: string): Promise<SyncProjectResult> => {
        try {
            const response = await axiosInstance.post(`/github/project/${projectId}/sync`, {});

            if (response.data.success) {
                toast({
                    title: 'Sync Successful',
                    description: 'Project synced with GitHub successfully',
                });

                return { success: true, data: response.data.data };
            }

            return { success: false, error: 'Sync failed' };
        } catch (error: any) {
            const detail = error.response?.data?.detail;

            if (error.response?.status === 409 && detail?.error?.code === 'SYNC_CONFLICT') {
                return {
                    success: false,
                    conflict: true,
                    conflictedFiles: detail.error.details?.conflictedFiles || [],
                    error: detail.error.message,
                };
            }

            const errorMessage = detail?.error?.message || 'Failed to sync project with GitHub';

            toast({
                title: 'Sync Failed',
                description: errorMessage,
                variant: 'destructive',
            });

            return { success: false, error: errorMessage };
        }
    }, [toast]);

    // Cancels a merge left mid-conflict by a manual sync, instead of handing it to the AI
    const abortSyncConflict = useCallback(async (projectId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await axiosInstance.post(`/github/project/${projectId}/sync/abort`, {});
            return { success: !!response.data?.success };
        } catch (error: any) {
            const msg = error.response?.data?.detail?.error?.message || 'Failed to abort merge';
            return { success: false, error: msg };
        }
    }, []);

    // Disconnect project from GitHub
    const disconnectProject = useCallback(async (projectId: string) => {
        try {
            const response = await axiosInstance.delete(`/github/project/${projectId}/connection`);

            if (response.data.success) {
                toast({
                    title: 'Disconnected',
                    description: 'Project disconnected from GitHub successfully',
                });

                return { success: true };
            }

            return { success: false, error: 'Disconnect failed' };
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail?.error?.message || 'Failed to disconnect project';

            toast({
                title: 'Disconnect Failed',
                description: errorMessage,
                variant: 'destructive',
            });

            return { success: false, error: errorMessage };
        }
    }, [toast]);

    // List user's GitHub repositories
    const listRepositories = useCallback(async (params?: {
        page?: number;
        perPage?: number;
        sort?: string;
        visibility?: string;
    }) => {
        try {
            const queryParams = new URLSearchParams(params as any).toString();
            const response = await axiosInstance.get(`/github/repositories?${queryParams}`);

            if (response.data.success) {
                return {
                    repositories: response.data.data.repositories,
                    total: response.data.data.pagination.total,
                };
            }

            return null;
        } catch (error: any) {
            toast({
                title: 'Failed to Load Repositories',
                description: error.response?.data?.detail?.error?.message || 'Failed to load GitHub repositories',
                variant: 'destructive',
            });

            return null;
        }
    }, [toast]);

    const listBranchesWithDefault = useCallback(async (repoFullName: string): Promise<GitHubBranchList> => {
        try {
            const [owner, repo] = repoFullName.split('/');
            const response = await axiosInstance.get(`/github/repositories/${owner}/${repo}/branches`);
            const data = response.data?.data ?? {};
            return { branches: data.branches ?? [], defaultBranch: data.defaultBranch ?? null, truncated: !!data.truncated };
        } catch {
            return { branches: [], defaultBranch: null, truncated: false };
        }
    }, []);

    const checkRepositoryAccess = useCallback(async (repoFullName: string): Promise<RepositoryAccess> => {
        try {
            const [owner, repo] = repoFullName.split('/');
            const response = await axiosInstance.get(`/github/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/access`);
            return response.data.data as RepositoryAccess;
        } catch {
            return { accessible: false, reason: 'unavailable', repository: null };
        }
    }, []);

    const detectRepositorySources = useCallback(async (repoFullNames: string[]): Promise<Record<string, RepositorySource>> => {
        if (repoFullNames.length === 0) return {};
        try {
            const response = await axiosInstance.post('/github/repositories/sources', { repos: repoFullNames.slice(0, 100) });
            return response.data?.data?.sources ?? {};
        } catch {
            return {};
        }
    }, []);

    const probeRepositoryBackend = useCallback(async (repoFullName: string, branch: string): Promise<RepositoryBackend | null> => {
        try {
            const [owner, repo] = repoFullName.split('/');
            const response = await axiosInstance.get(
                `/github/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/backend`,
                { params: { branch } },
            );
            const backend = response.data?.data?.backend;
            return backend && typeof backend.language === 'string' ? backend : null;
        } catch {
            return null;
        }
    }, []);

    const importProject = useCallback(async (params: {
        repositoryFullName: string;
        projectName: string;
        branch?: string;
        workspaceId?: number;
        projectType?: 'fullstack' | 'mobile';
        viaMigrationService?: boolean;
        architectMigration?: boolean;
        silent?: boolean;
    }): Promise<{ success: boolean; data?: any; error?: string }> => {
        try {
            const response = await axiosInstance.post('/github/import', {
                repositoryFullName: params.repositoryFullName,
                projectName: params.projectName,
                branch: params.branch || 'main',
                workspaceId: params.workspaceId ?? undefined,
                projectType: params.projectType ?? 'fullstack',
                viaMigrationService: params.viaMigrationService ?? false,
                architectMigration: params.architectMigration ?? false,
            });

            if (response.data.success) {
                if (!params.silent) toast({
                    title: 'Import Started',
                    description: `"${params.projectName}" is being imported. Cloning your repository in the background.`,
                });
                return { success: true, data: response.data.data };
            }

            return { success: false, error: 'Import failed' };
        } catch (error: any) {
            const isUpgradeRequired = error.response?.status === 402;
            const msg = error.response?.data?.detail?.error?.message || 'Failed to import repository';
            if (!params.silent) toast({
                title: isUpgradeRequired ? 'Upgrade Required' : 'Import Failed',
                description: msg,
                variant: 'destructive',
                action: isUpgradeRequired ? (
                    <ToastAction altText="Upgrade" onClick={() => { window.location.href = '/billing/plans'; }}>
                        Upgrade
                    </ToastAction>
                ) : undefined,
            });
            return { success: false, error: msg };
        }
    }, [toast]);

    const value: GitHubContextType = {
        isConnected,
        githubAccount,
        manageAccessUrl,
        isLoading,
        checkConnection,
        initiateConnection,
        disconnect,
        createRepository,
        exportProject,
        getProjectConnection,
        syncProject,
        abortSyncConflict,
        disconnectProject,
        listRepositories,
        linkRepository, // Add this
        listBranchesWithDefault,
        checkRepositoryAccess,
        detectRepositorySources,
        probeRepositoryBackend,
        importProject,
    };

    return <GitHubContext.Provider value={value}>{children}</GitHubContext.Provider>;
};

// Export handleCallback for use in callback route
export { GitHubContext };